import EthereumService from '../ethereum'
import bignumber from '@/utils/bignumber'
import rollbar from '@/services/rollbar'
import {
  ENV,
  KULAP_MAINNET_ADDR,
  KULAP_KOVAN_ADDR,
  KULAP_ABI,
  DEX_TOKEN_APPROVE_AMOUNT,
  DEX_TOKEN_APPROVE_GAS_LIMIT,
  BIGNUMBER_ZERO,
  PARTNER_INDEX
} from '../../constants'

export default class KulapService extends EthereumService {
  constructor () {
    super()
    this.kulapSmartContractAddr = null
    this.contract = null
    this.contractForRead = null
  }
  /**
   * Connect ABI smart contract function.
   * If you want to use `this.contract` or `this.kulapSmartContractAddr`, You  must call function `init` first.
   * Note: https://stackoverflow.com/questions/43431550/async-await-class-constructor
   * If env != development use mainnet only, Else use current network connected.
   */
  async init () {
    if (this.web3 && Object.keys(this.web3).length !== 0) {
      if (['development', 'develop', 'alpha', 'insider'].includes(ENV)) {
        const network = await this.getCurrentNetworkConnected()
        if (network === 'main') {
          this.kulapSmartContractAddr = KULAP_MAINNET_ADDR
        } else if (network === 'kovan') {
          this.kulapSmartContractAddr = KULAP_KOVAN_ADDR
        } else {
          console.error('Network not recognized!')
        }
      } else {
        this.kulapSmartContractAddr = KULAP_MAINNET_ADDR
      }
      if (this.kulapSmartContractAddr) {
        this.contract = await new this.web3.eth.Contract(KULAP_ABI, this.kulapSmartContractAddr)
        this.contractForRead = await new this.web3ForRead.eth.Contract(KULAP_ABI, this.kulapSmartContractAddr)
      }
    } else if (process.env.NODE_ENV !== 'test') {
      console.error(`web3 is ${this.web3}`)
    }
  }

  tradeEthertToAny (userAddr, tradeProxyIndex, fromAddr, toAddr, fromAmount, minReturnAmount, gasPrice = bignumber(5500000000), gasLimit, onTxHashed, onTxConfirmed, onReceiptFailed, dataForUI = {}) {
    const options = {
      from: userAddr,
      value: this.toBN(fromAmount),
      gasPrice: this.toBN(gasPrice), // default 5500000000 || 5.5 Gwei
      gas: this.toBN(gasLimit)
    }
    return this.trade(tradeProxyIndex, fromAddr, fromAmount, toAddr, minReturnAmount, options, dataForUI, onTxHashed, onTxConfirmed, onReceiptFailed)
  }

  tradeTokenToAny (userAddr, tradeProxyIndex, fromAddr, toAddr, fromAmount, minReturnAmount, gasPrice = bignumber(5500000000), gasLimit, onTxHashed, onTxConfirmed, onReceiptFailed, dataForUI = {}) {
    const options = {
      from: userAddr,
      value: BIGNUMBER_ZERO,
      gasPrice: this.toBN(gasPrice), // default 5500000000 || 5.5 Gwei
      gas: this.toBN(gasLimit)
    }
    return this.trade(tradeProxyIndex, fromAddr, fromAmount, toAddr, minReturnAmount, options, dataForUI, onTxHashed, onTxConfirmed, onReceiptFailed)
  }

  trade (tradeProxyIndex, fromAddr, fromAmount, toAddr, minReturnAmount, options, dataForUI, onTxHashed, onTxConfirmed, onReceiptFailed) {
    return new Promise((resolve, reject) => {
      const event = this.contract.methods.trade(
        tradeProxyIndex,
        fromAddr,
        this.toBN(fromAmount),
        toAddr,
        minReturnAmount,
        PARTNER_INDEX
      ).send(
        options
      ).on('transactionHash', (hash) => {
        if (dataForUI !== {}) dataForUI.hash = hash
        onTxHashed(hash, dataForUI)
      }).on('receipt', (receipt) => {
        event.removeAllListeners()
        onTxConfirmed(receipt, dataForUI)
        resolve(receipt)
      }).on('error', (error) => {
        if (error.message.search('"status": false') !== -1) {
          event.removeAllListeners()
          onReceiptFailed(error, dataForUI)
          return
        } else if (error.message.search('Network Error') !== -1) {
          rollbar.error(error.message)
        }
        reject(error)
      })
    })
  }

  async tokenAllowance (fromAddr, userAddr) {
    const tokenContract = await this.getTokenContract(fromAddr)
    const result = await tokenContract.methods.allowance(userAddr, this.kulapSmartContractAddr).call()
    if (bignumber(result).isEqualTo(BIGNUMBER_ZERO)) {
      return false
    }
    return true
  }

  async approveToken (fromAddr, userAddr, gasPrice, onHash = () => {}, onConfirm = () => {}, onError = () => {}) { // Set default event functions.
    const options = {
      from: userAddr,
      gasPrice: this.toBN(gasPrice),
      gas: this.toBN(DEX_TOKEN_APPROVE_GAS_LIMIT)
    }

    return new Promise(async (resolve, reject) => {
      const tokenContract = await this.getTokenContract(fromAddr)
      await tokenContract.methods.approve(
        this.kulapSmartContractAddr,
        this.toBN(DEX_TOKEN_APPROVE_AMOUNT)
      ).send(options).on('transactionHash', (hash) => {
        onHash(hash)
      }).on('confirmation', (confirmationNumber, receipt) => {
        if (confirmationNumber === 0) {
          onConfirm(receipt)
          resolve(receipt)
        }
      }).on('error', (error) => {
        onError(error)
        reject(error)
      })
    })
  }

  async getPastEvents (type, filter, fromBlock, toBlock) {
    const events = await this.contract.getPastEvents(type, { filter, fromBlock, toBlock })
    return events
  }

  // Helper Bignumber to bn
  toBN (amount) {
    return this.web3.utils.toBN(amount.toString(10))
  }

  adjustGasLimit (gasLimit) {
    return this.web3.utils.toBN(bignumber(gasLimit).times('1.25').toFixed(0))
  }
}



// WEBPACK FOOTER //
// ./src/services/kulap/index.js